import { useMemo } from 'react'
import type { QuestionProps } from '../eform.types'

// Legacy stores height as total inches (58 = 4'10" through 89 = 7'5").
// Option values must be total inches to match legacy DB format.
const HEIGHT_OPTIONS: { label: string; inches: number }[] = []
for (let ft = 4; ft <= 7; ft++) {
  const maxIn = ft === 7 ? 5 : 11
  for (let inch = ft === 4 ? 10 : 0; inch <= maxIn; inch++) {
    HEIGHT_OPTIONS.push({ label: `${ft}'${inch}"`, inches: ft * 12 + inch })
  }
}

function calcBmi(totalInches: number, weight: number): string {
  if (!Number.isFinite(totalInches) || !Number.isFinite(weight)) return ''
  if (totalInches <= 0 || weight <= 0) return ''
  const bmi = (weight * 703) / (totalInches * totalInches)
  return bmi.toFixed(1)
}

export default function HeightWeightBmi({  value, onChange, error }: QuestionProps) {
  const strValue = Array.isArray(value) ? value[0] ?? '' : value
  const parts = strValue.split('|')
  const totalInches = parseInt(parts[0] || '0', 10)
  const weight = parts[1] || ''

  const bmi = useMemo(() => calcBmi(totalInches, parseFloat(weight)), [totalInches, weight])

  function update(newInches: number, newWeight: string) {
    onChange(`${newInches}|${newWeight}`)
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Height</label>
          <select
            value={totalInches}
            onChange={(e) => update(parseInt(e.target.value, 10), weight)}
            className={`w-full rounded-lg border px-4 py-3 text-sm bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
              error ? 'border-red-300' : 'border-slate-200'
            }`}
          >
            <option value="0">Select height</option>
            {HEIGHT_OPTIONS.map((h) => (
              <option key={h.inches} value={h.inches}>{h.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Weight (lbs)</label>
          <input
            type="number"
            value={weight}
            onChange={(e) => update(totalInches, e.target.value)}
            placeholder="Weight in lbs"
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
              error ? 'border-red-300' : 'border-slate-200'
            }`}
          />
        </div>
      </div>
      {bmi && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-2.5">
          <span className="text-sm text-blue-700">
            BMI: <strong>{bmi}</strong>
          </span>
        </div>
      )}
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}
